import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { InventoryService } from '@servicios/inventory/inventory.service';
import { UserService } from '@servicios/user.service';
import { buildEquipmentFormPayload } from '../../utils/equipment-form.config';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-equipment-form',
  templateUrl: './equipment-form.component.html',
  styleUrls: ['./equipment-form.component.css'],
  standalone: false
})
export class EquipmentFormComponent implements OnInit, OnChanges {
  private inventoryService = inject(InventoryService);
  private userService = inject(UserService);
  private fb = inject(FormBuilder);

  @Input() visible = false;
  @Input() loading = false;

  _item: any = null;
  @Input() set item(val: any) {
    this._item = val;
    if (this.equipmentForm) {
      const payload = buildEquipmentFormPayload(val);
      this.equipmentForm.patchValue(payload);
    }
  }
  get item() { return this._item; }

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onSave = new EventEmitter<any>();

  equipmentForm!: FormGroup;

  brands = signal<any[]>([]);
  vendors = signal<any[]>([]);
  locations = signal<any[]>([]);
  users = signal<any[]>([]);

  constructor() {
    this.initForm();
  }

  private initForm() {
    this.equipmentForm = this.fb.group({
      id: [null],
      equipment_code: [''],
      name: ['', Validators.required],
      description: [''],
      brandId: [null],
      model: ['', Validators.required],
      serial_number: [''],
      vendorId: [null],
      locationId: [null],
      purchase_date: [null],
      acceptance_date: [null],
      condition_on_arrival: [''],
      warranty_expiration: [null],
      responsible_user_id: [null],
      calibration_period_days: [null],
      maintenance_period_days: [null]
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible'] && changes['visible'].currentValue && this.brands().length === 0) {
      this.loadMasters();
    }
    if (changes['item'] && changes['item'].currentValue) {
       const payload = buildEquipmentFormPayload(changes['item'].currentValue);
       this.equipmentForm.patchValue(payload);
    }
  }

  ngOnInit() {}

  async loadMasters() {
    try {
      // Catch errors individually so that one failure doesn't break the entire form
      const brandsReq = this.inventoryService.getAll('brand', { page: 1, limit: 100 }).catch(e => { console.error('Brands error', e); return { data: [] }; });
      const vendorsReq = this.inventoryService.getAll('supplier', { page: 1, limit: 100 }).catch(e => { console.error('Vendors error', e); return { data: [] }; });
      const locationsReq = this.inventoryService.getAll('location', { page: 1, limit: 100 }).catch(e => { console.error('Locations error', e); return { data: [] }; });
      const usersReq = firstValueFrom(this.userService.getUsersList()).catch(e => { console.error('Users error', e); return { data: { usersList: [] } }; });

      const [brandsRes, vendorRes, locationRes, usersRes] = await Promise.all([
        brandsReq, vendorsReq, locationsReq, usersReq
      ]);

      this.brands.set(brandsRes?.data ?? []);
      this.vendors.set(vendorRes?.data ?? []);
      this.locations.set(locationRes?.data ?? []);

      const formattedUsers = (usersRes?.data?.usersList || []).map((u: any) => ({
        ...u,
        fullName: `${u.lastName}, ${u.name}`
      }));
      this.users.set(formattedUsers);
    } catch (e) {
      console.error('Fatal error loading master data for form:', e);
    }
  }

  hide() {
    this.visibleChange.emit(false);
  }

  save() {
    if (this.equipmentForm.invalid) {
      this.equipmentForm.markAllAsTouched();
      return;
    }
    const val = { ...this.equipmentForm.value };
    if (typeof val.serial_number === 'string' && val.serial_number.trim() === '') {
      val.serial_number = null;
    }
    this.onSave.emit(val);
  }
}
